import React, { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { useIdentityContext } from '@/src/features/app/features/identity/store/store';
import { APP } from '@/src/features/app/app.constants';
import { Loader } from '@/src/features/_predefined/loader/Loader';
import { IdentityNavigator } from '@/src/features/domain/IdentityNavigator';
import { AuthenticatedRoutesGuard } from '@/src/features/app/features/identity/features/AuthenticatedRoutesGuard';
import { UnauthenticatedRoutesGuard } from '@/src/features/app/features/identity/features/UnauthenticatedRoutesGuard';

const LazyLoginPage = lazy(async () => {
  const { LoginPage } = await import('@/src/features/pages/login/Page');
  return { default: LoginPage };
});

const LazyErrorPage = lazy(async () => {
  const { ErrorPage } = await import('@/src/features/pages/error/Page');
  return { default: ErrorPage };
});

const LazyForgetPasswordPage = lazy(async () => {
  const { ForgetPasswordPage } = await import('@/src/features/pages/forget/Page');
  return { default: ForgetPasswordPage };
});

const LazyRegisterPage = lazy(async () => {
  const { RegisterPage } = await import('@/src/features/pages/register/Page');
  return { default: RegisterPage };
});

const LazyResetPasswordPage = lazy(async () => {
  const { ResetPasswordPage } = await import('@/src/features/pages/reset/Page');
  return { default: ResetPasswordPage };
});

const LazyAuthenticatedPage = lazy(async () => {
  const { AuthenticatedPage } = await import('@/src/features/pages/authenticated/Page');
  return { default: AuthenticatedPage };
});

const RedirectToApp = () => {
  const user = useIdentityContext((s) => s.user);

  if (user) {
    IdentityNavigator.goToAuthenticatedPage();
    return <Loader/>;
  }

  IdentityNavigator.goToLoginPage();
};

const router = createBrowserRouter([
  {
    path: APP.ROUTES.ROOT,
    element: <RedirectToApp/>,
    errorElement: <LazyErrorPage/>,
  },
  {
    element: <UnauthenticatedRoutesGuard><Outlet/></UnauthenticatedRoutesGuard>,
    children: [
      { path: APP.ROUTES.LOGIN, element: <LazyLoginPage/> },
      { path: APP.ROUTES.REGISTER, element: <LazyRegisterPage/> },
      { path: APP.ROUTES.FORGET_PASSWORD, element: <LazyForgetPasswordPage/> },
      { path: APP.ROUTES.RESET_PASSWORD, element: <LazyResetPasswordPage/> },
    ],
  },
  {
    element: <AuthenticatedRoutesGuard><Outlet/></AuthenticatedRoutesGuard>,
    children: [
      { path: APP.ROUTES.AUTHENTICATED, element: <LazyAuthenticatedPage/> },
    ],
  },
  { path: '*', element: <LazyErrorPage status={404}/> },
]);

export const Router = (): React.JSX.Element => {
  const initialized = useIdentityContext((s) => s.initialized);

  return (
    <Suspense fallback={<Loader/>}>
      {initialized ? <RouterProvider router={router}/> : <Loader/>}
    </Suspense>
  );
};
